import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@mui/material/Icon';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { PaymentReceiveModal } from 'sectors/pilates/members/components/paymentReceive/PaymentReceiveModal';
import MDBadge from 'components/MDBadge';

const ROW_H = 62;

function toNumber(v) {
  if (v == null) return 0;
  if (typeof v === 'string') {
    const cleaned = v.replace(/\./g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return Number(v) || 0;
}

function formatTry(value) {
  const n = Number(value) || 0;
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(n);
}

function getTrainerName(m) {
  // ✅ sende gerçek alan adı hangisiyse bunu tek satır yap
  return (
    m?.trainerName ||
    m?.trainerFullName ||
    m?.trainer?.fullName ||
    m?.trainer?.name ||
    m?.coachName ||
    m?.coach?.fullName ||
    m?.coach?.name ||
    ''
  );
}

export default function PackagePaymentTab() {
  const theme = useMuiTheme();

  const [q, setQ] = useState('');
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page] = useState(0);
  const [size] = useState(50);

  const [payOpen, setPayOpen] = useState(false);
  const [payMember, setPayMember] = useState(null);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await membersApi.list({
          page,
          size,
          active: true,
          // ✅ q YOK — istek atmayacak
        });
        if (!alive) return;
        setMembers(res?.items || []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Üyeler alınamadı.');
        setMembers([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [page, size]); // ✅ sadece page/size
  const filteredMembers = useMemo(() => {
    const needle = (q || '').trim().toLocaleLowerCase('tr-TR');
    if (!needle) return members;

    return (members || []).filter((m) => {
      const fullName = (m?.fullName || '').toLocaleLowerCase('tr-TR');
      const phone = (m?.phone || '').toLocaleLowerCase('tr-TR');
      const idStr = String(m?.id ?? '').toLocaleLowerCase('tr-TR');

      return fullName.includes(needle) || phone.includes(needle) || idStr.includes(needle);
    });
  }, [members, q]);
  // ✅ borçlular üstte (kart rengi değişmeden)
  const sortedMembers = useMemo(() => {
    const list = Array.isArray(filteredMembers) ? [...filteredMembers] : [];
    return list.sort((a, b) => {
      const da = toNumber(a?.debtTry);
      const db = toNumber(b?.debtTry);
      const aDebtor = da > 0;
      const bDebtor = db > 0;
      if (aDebtor !== bDebtor) return aDebtor ? -1 : 1;
      if (aDebtor && bDebtor) return db - da;
      return String(a?.fullName || '').localeCompare(String(b?.fullName || ''), 'tr');
    });
  }, [filteredMembers]);

  return (
    <MDBox sx={{ display: 'grid', gap: 1 }}>
      <MDInput
        label="Üye ara (ad / id)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        fullWidth
      />

      <MDBox sx={{ maxHeight: 360, overflow: 'auto', pr: 0.5 }}>
        {loading ? (
          <MDBox sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
              Üyeler yükleniyor…
            </MDTypography>
          </MDBox>
        ) : null}

        {error ? (
          <MDTypography variant="caption" color="error" sx={{ opacity: 0.9, mb: 1 }}>
            {error}
          </MDTypography>
        ) : null}

        {sortedMembers.map((m) => {
          const active = selected?.id === m.id;
          const debt = toNumber(m?.debtTry);
          const trainer = getTrainerName(m);

          return (
            <MDBox
              key={m.id}
              onClick={() => {
                setSelected(m);
                setPayMember(m);
                setPayOpen(true);
              }}
              sx={{
                height: ROW_H,
                px: 1.25,
                py: 1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.info.main, active ? 0.35 : 0.16)}`,
                bgcolor: active
                  ? alpha(theme.palette.info.main, 0.08)
                  : alpha(theme.palette.grey[500], 0.04),
                cursor: 'pointer',
                mb: 0.85,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.25,
                transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 0 0 4px ${alpha(theme.palette.info.main, 0.07)}`,
                },
              }}
            >
              {/* SOL: isim + telefon + borç badge (tek satır / taşanı kes) */}
              <MDBox sx={{ minWidth: 0, flex: 1 }}>
                <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {m.fullName}
                  </MDTypography>

                  {debt > 0 ? (
                    <MDBadge
                      badgeContent={`Borç: ${formatTry(m.debtTry)} ₺`}
                      color="error"
                      size="xs"
                      container
                    />
                  ) : null}
                </MDBox>

                <MDTypography
                  variant="caption"
                  sx={{
                    opacity: 0.72,
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.phone || '—'}
                </MDTypography>
              </MDBox>

              {/* ORTA: eğitmen (tek satır, küçük) */}
              <MDBox sx={{ minWidth: 0, maxWidth: 220, display: { xs: 'none', sm: 'block' } }}>
                <MDTypography
                  variant="caption"
                  sx={{
                    opacity: 0.7,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  Eğitmeni: <b>{trainer || '—'}</b>
                </MDTypography>
              </MDBox>

              {/* SAĞ: check */}
              <Icon
                sx={{
                  opacity: active ? 1 : 0.35,
                  color: active ? theme.palette.success.main : 'inherit',
                }}
              >
                check_circle
              </Icon>
            </MDBox>
          );
        })}

        {!sortedMembers.length && !loading ? (
          <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
            Üye bulunamadı.
          </MDTypography>
        ) : null}
      </MDBox>

      <Divider />

      <MDBox sx={{ display: 'flex', gap: 1 }}>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!selected}
          fullWidth
          onClick={() => {
            if (!selected) return;
            setPayMember(selected);
            setPayOpen(true);
          }}
          sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 900, height: 44 }}
        >
          Paket Ödemesi Al
        </MDButton>
      </MDBox>

      <PaymentReceiveModal
        open={payOpen}
        member={payMember}
        initialStep="PLAN"
        onClose={() => {
          setPayOpen(false);
          setPayMember(null);
        }}
        onSuccess={() => {
          // istersen burada refetch tetikle
        }}
      />
    </MDBox>
  );
}
